/**
 * Das Admin-Interface: Nutzer sehen, Plattform-Rolle vergeben, Konto sperren.
 *
 * Sichtbar nur für Admins — die Karte wird gar nicht erst gerendert. Das ist
 * **kein** Schutz, sondern Höflichkeit: Die Endpunkte dahinter prüfen die Rolle
 * selbst und gegen die Datenbank. Eine Oberfläche, die etwas versteckt, hat noch
 * nichts abgesichert.
 *
 * Noch eine Karte auf der Übersicht statt einer eigenen Adresse; ein Router kommt
 * mit HM-010.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, Input, Select } from "@hausmeter/ui";
import { useState } from "react";
import type { ApiComponents } from "@hausmeter/sdk";
import { api } from "../lib/client.js";
import { listRow, mutedText, plainList, rowLabel } from "../lib/list-styles.js";
import type { CurrentUser } from "../lib/session.js";

type PlatformRole = ApiComponents["schemas"]["PlatformRole"];
type UserStatus = ApiComponents["schemas"]["AdminUser"]["status"];

/**
 * `resident` steht in der Auswahl, lässt sich aber nur jemandem geben, der
 * keinen eigenen Account besitzt — sonst sagt der Server `409` (HM-116).
 */
const ROLE_OPTIONS: readonly { value: PlatformRole; label: string }[] = [
  { value: "resident", label: "Bewohner — keine eigene Anlage" },
  { value: "standard", label: "Standard — eine Anlage" },
  { value: "extended", label: "Erweitert — beliebig viele" },
  { value: "admin", label: "Administrator" },
];

/** Der Kontostatus in Worten. Der Rohwert bleibt als Titel an der Marke hängen. */
const STATUS_LABELS: Record<string, string> = {
  active: "aktiv",
  locked: "gesperrt",
  pending_verification: "unbestätigt",
  deleted: "gelöscht",
};

export function AdminCard({ user }: { user: CurrentUser }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  const users = useQuery({
    queryKey: ["admin-users", search],
    queryFn: async () => {
      const { data, error: failed } = await api.GET("/admin/users", {
        params: { query: { limit: 50, ...(search ? { search } : {}) } },
      });
      if (failed || !data) throw new Error("Die Nutzer konnten nicht geladen werden.");
      return data;
    },
  });

  const changeRole = useMutation({
    mutationFn: async ({ userId, platformRole }: { userId: string; platformRole: PlatformRole }) => {
      const { error: failed } = await api.PATCH("/admin/users/{userId}", {
        params: { path: { userId } },
        body: { platformRole },
      });
      // Die Meldung kommt vom Server — etwa beim Versuch, sich selbst herabzustufen.
      if (failed) throw new Error(messageOf(failed) ?? "Ändern fehlgeschlagen.");
    },
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (cause: Error) => setError(cause.message),
  });

  const toggleLock = useMutation({
    mutationFn: async ({ userId, locked }: { userId: string; locked: boolean }) => {
      const { error: failed } = await api.PATCH("/admin/users/{userId}", {
        params: { path: { userId } },
        body: { status: locked ? "active" : "locked" },
      });
      if (failed) throw new Error(messageOf(failed) ?? "Ändern fehlgeschlagen.");
    },
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (cause: Error) => setError(cause.message),
  });

  return (
    <Card
      title="Nutzer"
      subtitle="Rollen und Kontostatus. Verbrauchsdaten fremder Accounts sind auch hier nicht einsehbar."
    >
      <Input
        label="Suche"
        value={search}
        placeholder="E-Mail-Adresse durchsuchen"
        onChange={(event) => setSearch(event.target.value)}
      />

      {users.isPending && <p style={{ ...mutedText, marginTop: "var(--sp-3)" }}>Wird geladen …</p>}
      {users.data?.items.length === 0 && (
        <p style={{ ...mutedText, marginTop: "var(--sp-3)" }}>Kein Treffer.</p>
      )}

      {users.data && users.data.items.length > 0 && (
        <ul style={{ ...plainList, marginTop: "var(--sp-3)" }}>
          {users.data.items.map((entry) => {
            const isSelf = entry.id === user.id;
            const activeSites = entry.accounts.reduce(
              (sum, account) => sum + account.activeSiteCount,
              0,
            );

            return (
              <li key={entry.id} style={listRow}>
                <span style={{ ...rowLabel, minWidth: "min(220px, 100%)" }}>{entry.email}</span>

                <Select
                  label={`Plattform-Rolle von ${entry.email}`}
                  labelHidden
                  size="sm"
                  options={ROLE_OPTIONS}
                  value={entry.platformRole}
                  // Die eigene Rolle bleibt gesperrt: Der Server lehnt sie ohnehin
                  // ab, und ein Feld, das immer scheitert, ist eine Falle.
                  disabled={isSelf || changeRole.isPending}
                  onChange={(event) =>
                    changeRole.mutate({
                      userId: entry.id,
                      platformRole: event.target.value as PlatformRole,
                    })
                  }
                />

                <span style={mutedText}>
                  {activeSites === 1 ? "1 Anlage" : `${activeSites} Anlagen`}
                </span>

                <Badge tone={toneOf(entry.status)} title={entry.status}>
                  {STATUS_LABELS[entry.status] ?? entry.status}
                </Badge>

                {!isSelf && entry.status !== "deleted" && (
                  <span style={{ marginLeft: "auto" }}>
                    <Button
                      variant={entry.status === "locked" ? "secondary" : "ghost"}
                      size="sm"
                      onClick={() =>
                        toggleLock.mutate({ userId: entry.id, locked: entry.status === "locked" })
                      }
                    >
                      {entry.status === "locked" ? "Entsperren" : "Sperren"}
                    </Button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {error !== null && (
        <div style={{ marginTop: "var(--sp-3)" }}>
          <Alert>{error}</Alert>
        </div>
      )}
    </Card>
  );
}

/**
 * Farbe zum Kontostatus. `pending_verification` bleibt neutral und nicht gelb:
 * Solange es keinen Mailversand gibt (OQ-017), ist ein unbestätigtes Konto der
 * Normalfall und keine Auffälligkeit.
 */
function toneOf(status: UserStatus): "neutral" | "warning" | "danger" {
  if (status === "locked") return "warning";
  if (status === "deleted") return "danger";
  return "neutral";
}

/** Das Fehlerformat der API: `{ error: { code, message } }`. */
function messageOf(failed: unknown): string | undefined {
  return (failed as { error?: { message?: string } } | undefined)?.error?.message;
}
